import React, { useEffect, useRef, useState } from 'react';
import ProgressBar from '../../components/ProgressBar/progressbar';
import Styles from '../../modules/patchcenter/patchcenterStyle.module.scss';
import moment from 'moment';
import { usePatchForm } from '../../providers/PatchFormProvider';

import { useNavigate } from 'react-router-dom';
import { useAuth } from 'oidc-react';
import Project from '../../common/Utils/project';
import Select from 'react-select';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import { useAgentHeartBeatService } from '../services/agentHeartBeatService';
import { AgentStatusIndicator } from '../AgentStatus';
import api from '../services/apiService';
import Pagination from '../Pagination/Pagination';
import { DataTable, ColumnConfig } from '../Table';

const SelectDevice = () => {
  type HeartbeatInfo = {
    status: string;
    ipAddress: string;
    heartbeatStatusMessage: string;
  };

  const { patchFormData, setPatchFormData } = usePatchForm();

  const [isEmergency, setIsEmergency] = useState(patchFormData.RemediationPlanData?.IsEmergencyRemediationPlan ?? false);
  const [remedName, setRemedName] = useState(patchFormData.RemediationPlanData?.PlanName ?? '');
  const [remedNameError, setRemedNameError] = useState('');
  const [selectionError, setSelectionError] = useState('');
  const { getAgentHeartbeatStatus } = useAgentHeartBeatService();
  const [heartbeatData, setHeartbeatData] = useState<Record<string, HeartbeatInfo>>({});
  const [heartbeatLoading, setHeartbeatLoading] = useState<boolean>(false);
  const remedInputRef = useRef(null);

  // Add Active Directory as an option
  const [allOptions, setAllOptions] = useState<Record<string, string[]>>({
    'OS Flavour': ['Windows 11', 'Windows 10', 'Linux'],
    Group: [],
    'Active Directory': [],
  });

  const [searchValues, setSearchValues] = useState([]);
  const [group, setGroup] = useState([]);
  const [osFlavour, setOSflavour] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [showDevices, setShowDevices] = useState(false);
  const [selectedGroupData, setSelectedGroupData] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);

  const [selectedAgentIds, setSelectedAgentIds] = useState([]);

  const [osAgentsData, setOsAgentsData] = useState([]);

  const [agentsData, setAgentsData] = useState([]); // Agents list for selected group

  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);

  const [selectAllOSDevices, setSelectAllOSDevices] = useState(false);

  const [groupSearchQuery, setGroupSearchQuery] = useState('');
  const [osSearchQuery, setOsSearchQuery] = useState('');

  // ---- Active Directory specific states ----
  const [adEntries, setAdEntries] = useState([]); // raw entries from API (contains fullDistinguishedName)
  const [adDomains, setAdDomains] = useState([]); // domain options like patchifi.local
  const [adGroupsByDomain, setAdGroupsByDomain] = useState({} as Record<string, string[]>); // domain -> [groupNames]
  const [selectedADDomain, setSelectedADDomain] = useState<string | null>(null);
  const [selectedADGroups, setSelectedADGroups] = useState<Array<{ label: string; value: string }>>([]); // multi select groups (first OU)

  // -------------------------------------------

  const fetchHeartbeat = async (deviceIds: string[]) => {
    if (!deviceIds?.length) return;

    setHeartbeatLoading(true);
    try {
      const data = await getAgentHeartbeatStatus(selectedTenantId as string, deviceIds);

      // Keep a full heartbeat snapshot so UI can render status, IP, and hover message.
      const statusMap: Record<string, HeartbeatInfo> = {};

      data.data.forEach((agent: any) => {
        statusMap[agent.tenantAgentId || agent.agentId] = {
          status: agent.agentStatus || (agent.status ? 'Active' : 'Inactive'),
          ipAddress: agent.ipAddress || '-',
          heartbeatStatusMessage:
            agent.heartbeatStatusMessage || agent.heartBeatStatusMessage || agent.statusMessage || 'No heartbeat status message available',
        };
      });

      setHeartbeatData(statusMap);
    } catch (error) {
      console.error('Error fetching heartbeat:', error);
    } finally {
      setHeartbeatLoading(false);
    }
  };

  const handleSelectAllOSDevices = () => {
    if (selectAllOSDevices) {
      // Unselect all
      setSelectedDeviceIds([]);
      setPatchFormData((prevData) => ({
        ...prevData,
        deviceSelection: {
          ...prevData.deviceSelection,
          selectedDeviceIds: [],
        },
      }));
    } else {
      // Select all OS devices
      const allIds = osAgentsData.map((agent) => agent.agentId);
      setSelectedDeviceIds(allIds);
      setPatchFormData((prevData) => ({
        ...prevData,
        deviceSelection: {
          ...prevData.deviceSelection,
          selectedDeviceIds: allIds,
        },
      }));
    }
    setSelectAllOSDevices(!selectAllOSDevices);
  };

  useEffect(() => {
    setSelectAllOSDevices(osAgentsData.length > 0 && selectedDeviceIds.length === osAgentsData.length);
  }, [selectedDeviceIds, osAgentsData]);

  const [osCurrentPage, setOsCurrentPage] = useState(1);
  const rowsPerPage = 20;

  const filteredOsAgents = osAgentsData.filter((agent) => {
    if (!osSearchQuery.trim()) return true;
    const q = osSearchQuery.toLowerCase();
    return (
      agent.deviceName?.toLowerCase().includes(q) ||
      agent.os?.toLowerCase().includes(q) ||
      agent.osVersion?.toLowerCase().includes(q) ||
      agent.groupName?.toLowerCase().includes(q)
    );
  });
  const paginatedOsAgents = filteredOsAgents.slice((osCurrentPage - 1) * rowsPerPage, osCurrentPage * rowsPerPage);
  const osTotalPages = Math.ceil(filteredOsAgents.length / rowsPerPage);

  const [targetDevice, setTargetDevice] = useState({
    additionalProp1: [''],
    additionalProp2: [''],
    additionalProp3: [''],
  });

  const [selectAll, setSelectAll] = useState(false);
  const handleSelectAllAgents = () => {
    if (selectAll) {
      setSelectedAgentIds([]); // Unselect all
    } else {
      setSelectedAgentIds(agentsData.map((agent) => agent.tenantAgentId)); // Select all
    }
    setSelectAll(!selectAll);
  };

  useEffect(() => {
    setSelectAll(agentsData.length > 0 && selectedAgentIds.length === agentsData.length);
  }, [selectedAgentIds, agentsData]);

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      backgroundColor: '#161617',
      borderColor: state.isFocused ? '#4B5563' : '#4B5563',
      boxShadow: 'none',
      minHeight: 56,
      '&:hover': { borderColor: '#4B5563' },
    }),
    menu: (base) => ({ ...base, backgroundColor: '#161617' }),
    option: (base, { isFocused, isSelected }) => ({
      ...base,
      backgroundColor: isSelected ? '#444' : isFocused ? '#27272a' : 'transparent',
      color: '#fff',
      cursor: 'pointer',
    }),
    singleValue: (base) => ({
      ...base,
      color: '#fff',
    }),

    multiValue: (base) => ({ ...base, backgroundColor: '#2b2b2b', borderRadius: 8 }),
    multiValueLabel: (base) => ({ ...base, color: '#fff' }),
    multiValueRemove: (base) => ({
      ...base,
      color: '#ef4444',
      ':hover': { backgroundColor: 'transparent', color: '#b91c1c' },
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base) => ({ ...base, color: '#fff' }),
  };

  // Helper: parse DN and return {cn, ous:[], dcs:[], domain}
  const parseFullDistinguishedName = (dn) => {
    const parts = dn.split(',').map((p) => p.trim());
    const cnPart = parts.find((p) => p.toUpperCase().startsWith('CN='));
    const ouParts = parts.filter((p) => p.toUpperCase().startsWith('OU='));
    const dcParts = parts.filter((p) => p.toUpperCase().startsWith('DC='));

    const cn = cnPart ? cnPart.substring(3) : null;
    const ous = ouParts.map((p) => p.substring(3));
    const dcs = dcParts.map((p) => p.substring(3));
    const domain = dcs.length > 0 ? dcs.join('.') : null;

    return { cn, ous, dcs, domain };
  };

  const handleShowDevices = async () => {
    if (searchValues.length === 0 && selectedType !== 'Group' && selectedType !== 'Active Directory') {
      console.warn('Please select at least one value');

      setOsAgentsData([]);
      setAgentsData([]);
      setSelectedAgentIds([]);
      setSelectedDeviceIds([]);
      setShowDevices(false);

      return;
    }

    if (selectedType === 'OS Flavour') {
      const osFlavoursArray = searchValues.map((item) => item.value);
      const osAgents = await getOSAgents(osFlavoursArray);
      setOsAgentsData(osAgents);
      setPatchFormData((prev) => ({
        ...prev,
        deviceSelection: {
          ...prev.deviceSelection,
          osAgentsData: osAgents,
          selectedDeviceIds,
          selectedType,
          searchValues,
          showDevices: true,
        },
      }));
      setShowDevices(true);
      fetchHeartbeat(osAgents.map((agent: any) => agent.agentId));
    } else if (selectedType === 'Group') {
      let allAgents = [];
      for (const group of searchValues) {
        const agents = await getAgents(group.value);
        allAgents.push(...agents);
      }
      setAgentsData(allAgents);
      setShowDevices(true);
      fetchHeartbeat(allAgents.map((agent: any) => agent.tenantAgentId));
    } else if (selectedType === 'Active Directory') {
      // Build list of fullDistinguishedName(s) based on selected domain + selected groups (first OU)
      // We stored adEntries as array of objects { fullDistinguishedName, domain, groupName (first OU) }
      const chosenDomain = selectedADDomain || (adDomains.length === 1 ? adDomains[0] : null);
      if (!chosenDomain) {
        console.warn('Please select domain');
        return;
      }

      if (!selectedADGroups || selectedADGroups.length === 0) {
        console.warn('Please select group(s) for Active Directory');
        return;
      }

      // collect fullDNs for chosen domain & groups
      const selectedGroupNames = selectedADGroups.map((g) => g.value);
      const selectedFullDNs = adEntries
        .filter((e) => e.domain === chosenDomain && selectedGroupNames.includes(e.groupName))
        .map((e) => e.fullDistinguishedName);

      if (selectedFullDNs.length === 0) {
        console.warn('No AD entries found for selected domain/group');
        setOsAgentsData([]);
        setShowDevices(false);
        return;
      }

      try {
        const tenantId = selectedTenantId;
        const response = await api.post(
          `${Project.api}tenantservice/TenantAgent/${tenantId}/agent-by-active-directory-group`,
          JSON.stringify(selectedFullDNs),
        );
        const { data } = response;
        const agents = data?.isSuccess ? data.data || [] : [];
        // Treat AD agents like OS agents (they have agentId) to reuse OS table
        setOsAgentsData(agents);
        setShowDevices(true);
        setPatchFormData((prev) => ({
          ...prev,
          deviceSelection: {
            ...prev.deviceSelection,
            adDomain: chosenDomain,
            adGroups: selectedADGroups,
            osAgentsData: agents,
            selectedDeviceIds,
            selectedType,
            searchValues: selectedADGroups,
            showDevices: true,
          },
        }));

        fetchHeartbeat(agents.map((agent: any) => agent.agentId));
      } catch (err) {
        console.error('Error fetching AD agents:', err);
        setOsAgentsData([]);
      }
    }
  };

  const [selectedValues, setSelectedValues] = useState<Record<string, string[]>>({
    'OS Flavour': [],
    Group: [],
    'Active Directory': [],
  });

  useEffect(() => {
    setTargetDevice((prev) => ({
      ...prev,
      additionalProp1: [selectedType],
      additionalProp2: [...selectedValues['Group']],
    }));
  }, [selectedValues]);

  const [selectedType, setSelectedType] = useState<string>(patchFormData.deviceSelection?.selectedType || 'OS Flavour');

  useEffect(() => {
    setSearchValues([]);
  }, [selectedType]);

  const navigate = useNavigate();
  const auth = useAuth();
  const { selectedTenantId } = useTenantSelection();

  const handleSquareClick = (device) => {
    setSelectedGroupIds((prevSelected) => {
      if (prevSelected.includes(device.tenantGroupId)) {
        return prevSelected.filter((id) => id !== device.tenantGroupId);
      } else {
        return [...prevSelected, device.tenantGroupId];
      }
    });
  };

  const filteredGroupAgents = agentsData
    .filter((agent) => agent && agent.tenantAgentId)
    .filter((agent) => {
      if (!groupSearchQuery.trim()) return true;
      const q = groupSearchQuery.toLowerCase();
      return (
        agent.deviceName?.toLowerCase().includes(q) ||
        agent.os?.toLowerCase().includes(q) ||
        agent.osVersion?.toLowerCase().includes(q) ||
        agent.groupName?.toLowerCase().includes(q)
      );
    });
  const totalPages = Math.max(1, Math.ceil(filteredGroupAgents.length / rowsPerPage));
  const paginatedDevices = filteredGroupAgents.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const getOSflavours = async () => {
    try {
      if (auth && auth.userData) {
        const token = auth.userData.access_token;
        let tenantId = selectedTenantId;
        const response = await api.get(`${Project.api}tenantservice/TenantAgent/${tenantId}/os-flavour`);

        const osFlavours = response.data?.data || [];
        const options = osFlavours.map((item) => ({
          label: item.osName,
          value: item.osName,
        }));
        setOSflavour(options);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const getTenentGroups = async () => {
    try {
      if (auth && auth.userData) {
        const token = auth.userData.access_token;
        let tenantId = selectedTenantId;
        const response = await api.get(`${Project.api}tenantservice/TenantGroup/${tenantId}/groups`);

        let { data } = response;
        const groupNames = data.data[0].tenantGroupList.map((group) => group.groupName);
        setGroup(data.data);
        setAllOptions((prev) => ({
          ...prev,
          Group: groupNames,
        }));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  // New: fetch Active Directory groups and process them
  const getActiveDirectoryGroups = async () => {
    try {
      if (auth && auth.userData) {
        const tenantId = selectedTenantId;
        const response = await api.get(`${Project.api}tenantservice/TenantAgent/${tenantId}/agent-active-directory-group`);
        const raw = response.data?.data || [];

        // Process raw entries into { fullDistinguishedName, domain, groupName }
        const entries = raw
          .map((r) => ({ fullDistinguishedName: r.fullDistinguishedName }))
          .filter((e) => e.fullDistinguishedName)
          .map((e) => {
            const parsed = parseFullDistinguishedName(e.fullDistinguishedName);
            // groupName is the first OU (if exists)
            const groupName = parsed.ous && parsed.ous.length > 0 ? parsed.ous[0] : null;
            return {
              fullDistinguishedName: e.fullDistinguishedName,
              domain: parsed.domain,
              groupName,
            };
          })
          .filter((e) => e.domain && e.groupName);

        setAdEntries(entries);

        // derive domains and groups per domain
        const domains = Array.from(new Set(entries.map((e) => e.domain))).filter(Boolean);
        const groupsByDomain = {} as Record<string, string[]>;
        domains.forEach((d) => {
          groupsByDomain[d] = Array.from(new Set(entries.filter((e) => e.domain === d).map((x) => x.groupName))).filter(Boolean);
        });

        setAdDomains(domains);
        setAdGroupsByDomain(groupsByDomain);
        setAllOptions((prev) => ({
          ...prev,
          'Active Directory': domains,
        }));
      }
    } catch (err) {
      console.error('Error fetching AD groups:', err);
    }
  };

  const getOSAgents = async (osFlavoursArray) => {
    try {
      if (auth && auth.userData) {
        let tenantId = selectedTenantId;

        const osFlavoursPayload = {
          osFlavours: osFlavoursArray.map((item) => {
            // Try to get osName from item.osName, item.label, or item.value
            let osName = item.osName || item.label || item.value;
            // Fallback: if item is a string, use it directly
            if (!osName && typeof item === 'string') osName = item;
            return {
              osName,
              osType: item.osType || (osName && osName.includes('Server') ? 'Server' : 'Client'),
            };
          }),
        };
        const response = await api.post(
          `${Project.api}tenantservice/TenantAgent/${tenantId}/agent-by-os-flavour`,
          JSON.stringify(osFlavoursPayload),
        );

        let { data } = response;
        return data?.isSuccess ? (Array.isArray(data.data) && data.data[0]?.agents ? data.data[0].agents : data.data || []) : [];
      }
    } catch (error) {
      console.error('Error fetching OS agents:', error);
      return [];
    }
  };

  const getAgents = async (groupId) => {
    try {
      if (auth && auth.userData) {
        const token = auth.userData.access_token;
        let tenantId = selectedTenantId;
        const response = await api.get(`${Project.api}tenantservice/TenantGroup/${tenantId}/agents/${groupId}`);

        let { data } = response;
        return data?.isSuccess && data.data?.length > 0 ? data.data[0].agents || [] : [];
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      return [];
    }
  };

  useEffect(() => {
    getTenentGroups();
    getOSflavours();
    getActiveDirectoryGroups(); // fetch AD info on mount
  }, []);

  // Restore state from patchFormData
  useEffect(() => {
    if (patchFormData.deviceSelection) {
      setSelectedType(patchFormData.deviceSelection.selectedType || 'OS Flavour');

      setAgentsData(patchFormData.deviceSelection.agentsData || []);
      setOsAgentsData(patchFormData.deviceSelection.osAgentsData || []);
      setSelectedAgentIds(patchFormData.deviceSelection.selectedAgentIds || []);
      setSelectedDeviceIds(patchFormData.deviceSelection.selectedDeviceIds || []);
      setShowDevices(patchFormData.deviceSelection.showDevices || false);

      if (patchFormData.searchData?.selectedType === 'Group') {
        setSearchValues(patchFormData.groupSearchValues || []);
      } else if (patchFormData.deviceSelection?.selectedType === 'Active Directory') {
        // restore AD domain/groups if available
        const adDomain = patchFormData.deviceSelection.adDomain;
        const adGroups = patchFormData.deviceSelection.adGroups || [];
        if (adDomain) setSelectedADDomain(adDomain);
        if (adGroups.length > 0) setSelectedADGroups(adGroups);
        setSearchValues(adGroups);
      } else {
        setSearchValues(patchFormData.deviceSelection.searchValues || []);
      }
    }
  }, [adDomains, osFlavour, group]);

  useEffect(() => {
    const selectedIds = selectedDeviceIds.length > 0 ? selectedDeviceIds : selectedAgentIds;
    setPatchFormData((prev) => ({
      ...prev,
      deviceSelection: {
        selectedType,
        searchValues,
        agentsData,
        osAgentsData,
        selectedAgentIds,
        selectedDeviceIds: selectedIds,
        showDevices,
      },
    }));
  }, [selectedType, searchValues, agentsData, selectedAgentIds, showDevices, selectedDeviceIds]);

  const handleNext = () => {
    if (!remedName.trim()) {
      setRemedNameError('Remediation plan name cannot be empty');
      remedInputRef.current?.focus();
      return;
    }
    setRemedNameError('');

    if (
      (selectedType === 'Group' && selectedAgentIds.length === 0) ||
      ((selectedType === 'OS Flavour' || selectedType === 'Active Directory') && selectedDeviceIds.length === 0)
    ) {
      setSelectionError('Please select at least one device/agent');
      return;
    } else {
      setSelectionError('');
    }

    // Build RemediationPlanAgentList
    const groupAgents = agentsData
      .filter((agent) => selectedAgentIds.includes(agent.tenantAgentId))
      .map((agent) => ({
        TenantAgentID: agent.tenantAgentId,
      }));

    const osAgents = osAgentsData
      .filter((agent) => selectedDeviceIds.includes(agent.agentId))
      .map((agent) => ({
        TenantAgentID: agent.agentId,
      }));

    const allAgents = [...groupAgents, ...osAgents];

    setPatchFormData((prev) => ({
      ...prev,
      RemediationPlanData: {
        PlanName: remedName,
        IsEmergencyRemediationPlan: isEmergency,
        SendEmailOnCompletion: true,
        RemediationPlanStartTime: moment().utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
        IsActive: true,
      },
      targetDevice: {
        targetDeviceFilters: targetDevice,
      },
      RemediationPlanAgentList: allAgents,
    }));

    navigate('/patchCenter/AvailablePatch');
  };

  const handleCancelBtn = () => {
    navigate('/');
  };

  const getSquareStyle = (isActive) => ({
    width: '20px',
    height: '20px',
    borderRadius: '4px',
    border: '5px solid #444',
    backgroundColor: isActive ? '#00FF00' : 'transparent',
    cursor: 'pointer',
    display: 'inline-block',
  });

  const handleAgentClick = (agent) => {
    setSelectedAgentIds((prevSelected) => {
      if (prevSelected.includes(agent.tenantAgentId)) {
        return prevSelected.filter((id) => id !== agent.tenantAgentId);
      } else {
        return [...prevSelected, agent.tenantAgentId];
      }
    });
  };

  const handleDeviceSelect = (id) => {
    setSelectedDeviceIds((prev) => {
      const updated = prev.includes(id) ? prev.filter((deviceId) => deviceId !== id) : [...prev, id];
      setPatchFormData((prevData) => ({
        ...prevData,
        deviceSelection: {
          ...prevData.deviceSelection,
          selectedDeviceIds: updated,
        },
      }));
      return updated;
    });
  };

  const osColumns: ColumnConfig[] = [
    {
      key: 'select',
      header: <div onClick={handleSelectAllOSDevices} style={{ ...getSquareStyle(selectAllOSDevices), margin: '0 auto' }} />,
      width: '60px',
      align: 'center',
      render: (_, agent) => (
        <div className="cursor-pointer" onClick={() => handleDeviceSelect(agent.agentId)}>
          <div style={getSquareStyle(selectedDeviceIds.includes(agent.agentId))} />
        </div>
      ),
    },
    { key: 'deviceName', header: 'Device Name', width: '220px' },
    { key: 'os', header: 'Operating System', width: '1fr' },
    { key: 'osVersion', header: 'OS Version', width: '150px' },
    {
      key: 'ipAddress',
      header: 'Ip Address',
      width: '150px',
      render: (_, agent) => heartbeatData[agent.agentId]?.ipAddress || '-',
    },
    {
      key: 'groupName',
      header: 'Group Name',
      width: '200px',
      render: (value) => value || '-',
    },
    {
      key: 'status',
      header: 'Status',
      width: '100px',
      align: 'center',
      render: (_, agent) => (
        <AgentStatusIndicator
          agentId={agent.agentId}
          isActive={heartbeatData[agent.agentId]?.status === 'Active'}
          statusMessage={heartbeatData[agent.agentId]?.heartbeatStatusMessage}
          isLoading={heartbeatLoading}
        />
      ),
    },
  ];

  const groupColumns: ColumnConfig[] = [
    {
      key: 'select',
      header: <div onClick={handleSelectAllAgents} style={{ ...getSquareStyle(selectAll), margin: '0 auto' }} />,
      width: '60px',
      align: 'center',
      render: (_, agent) => (
        <div className="cursor-pointer" onClick={() => handleAgentClick(agent)}>
          <div style={getSquareStyle(selectedAgentIds.includes(agent.tenantAgentId))} />
        </div>
      ),
    },
    { key: 'deviceName', header: 'Device Name', width: '1fr', render: (value) => value || '-' },
    { key: 'os', header: 'Operating System', width: '1fr', render: (value) => value || '-' },
    { key: 'osVersion', header: 'OS Version', width: '1fr', render: (value) => value || '-' },
    {
      key: 'ipAddress',
      header: 'Ip Address',
      width: '1fr',
      render: (_, agent) => heartbeatData[agent.tenantAgentId]?.ipAddress || '-',
    },
    { key: 'groupName', header: 'Group Name', width: '1fr', render: (value) => value || '-' },
    {
      key: 'status',
      header: 'Status',
      width: '100px',
      align: 'center',
      render: (_, agent) => (
        <AgentStatusIndicator
          agentId={agent.tenantAgentId}
          isActive={heartbeatData[agent.tenantAgentId]?.status === 'Active'}
          statusMessage={heartbeatData[agent.tenantAgentId]?.heartbeatStatusMessage}
          isLoading={heartbeatLoading}
        />
      ),
    },
  ];

  useEffect(() => {
    localStorage.setItem('deviceSearchValues', JSON.stringify(searchValues));
  }, [searchValues]);

  useEffect(() => {
    const savedSearchValues = localStorage.getItem('deviceSearchValues');
    if (!savedSearchValues) return;

    const parsed = JSON.parse(savedSearchValues);

    if (selectedType === 'OS Flavour' && osFlavour.length > 0) {
      const restored = parsed.map((sel) => osFlavour.find((opt) => opt.value === sel.value) || sel);
      setSearchValues(restored);
    }

    if (selectedType === 'Group' && group.length > 0) {
      const groupOptions = group.flatMap((tenant) =>
        tenant.tenantGroupList.map((grp) => ({
          label: grp.groupName,
          value: grp.tenantGroupId,
        })),
      );
      const restored = parsed.map((sel) => groupOptions.find((opt) => opt.value === sel.value) || sel);
      setSearchValues(restored);
    }

    if (selectedType === 'Active Directory' && adDomains.length > 0) {
      // if saved values are domain/groups, try to restore
      const restored = parsed.map((sel) => ({ label: sel.label, value: sel.value }));
      setSelectedADGroups(restored);
    }
  }, [selectedType, osFlavour, group, adDomains]);

  return (
    <div className="flex flex-col w-full text-white p-8" style={{ background: 'var(--Card-Color, #1D1D1D)' }}>
      <div className="flex-grow flex flex-col w-full" style={{ background: 'var(--Card-Color, #1D1D1D)' }}>
        {/* Progress Bar */}
        <div className={`${Styles.progressBar} items-center justify-between mb-10`} style={{ marginLeft: '0px' }}>
          <ProgressBar stepNum={1} identity={'Device select'} />
        </div>

        {/* Form Section */}
        <div className="rounded-xl shadow-lg w-full" style={{ borderRadius: '20px', background: 'var(--Card-Color, #1D1D1D)' }}>
          <div>
            <label className="block mb-2 text-normal">Remediation Plan Name *</label>
            <input
              ref={remedInputRef}
              type="text"
              className={`${Styles.remed_input} w-full p-3 rounded-md bg-gray-700 text-white border border-gray-600 focus:ring-green-400`}
              style={remedName ? { borderBottom: '1px solid #08a532' } : {}}
              value={remedName}
              onChange={(e) => {
                setRemedName(e.target.value);
                if (remedNameError) setRemedNameError('');
              }}
            />
            {remedNameError && <p className="text-red-500 text-sm mt-1">{remedNameError}</p>}
          </div>

          <div className="flex items-center mt-4 cursor-pointer" onClick={() => setIsEmergency(!isEmergency)}>
            <div style={getSquareStyle(isEmergency)}></div>
            <label className="ml-2 text-normal select-none">Emergency Remediation Plan</label>
          </div>

          {/* OS Selection */}
          <div className="flex flex-col lg:flex-row lg:justify-between items-center gap-4 mt-6">
            {/* Select Criteria */}
            <div className="flex flex-col w-full lg:w-1/3">
              <label className="text-normal mb-1">Select Criteria</label>
              <select
                className="w-full p-3 text-white border border-gray-600 rounded-md"
                style={{ background: '#161617' }}
                value={selectedType}
                onChange={(e) => {
                  const newType = e.target.value;
                  setSelectedType(newType);
                  setSearchValues([]);
                  setSelectedAgentIds([]);
                  setSelectedDeviceIds([]);
                  setOsAgentsData([]);
                  setAgentsData([]);
                  setShowDevices(false);
                  setSelectAll(false);
                  setSelectAllOSDevices(false);
                  localStorage.removeItem('deviceSearchValues');
                }}
              >
                {Object.keys(allOptions).map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            {/* Search Value */}
            <div className="w-full lg:w-1/3">
              <label className="block mb-1">Search Value</label>

              {selectedType === 'OS Flavour' && (
                <Select
                  isMulti
                  options={osFlavour}
                  value={searchValues}
                  onChange={(selected) => {
                    setSearchValues(selected || []);
                  }}
                  styles={selectStyles}
                  className="react-select-container w-full"
                  classNamePrefix="react-select"
                />
              )}

              {selectedType === 'Group' && (
                <Select
                  isMulti
                  options={group.flatMap((tenant) =>
                    tenant.tenantGroupList.map((grp) => ({
                      label: grp.groupName,
                      value: grp.tenantGroupId,
                    })),
                  )}
                  value={patchFormData?.groupSearchValues}
                  onChange={(selected) => {
                    setSearchValues(selected || []);
                    setPatchFormData((prev) => ({
                      ...prev,
                      groupSearchValues: selected || [],
                    }));
                  }}
                  styles={selectStyles}
                  className="react-select-container w-full"
                  classNamePrefix="react-select"
                />
              )}

              {/* Active Directory UI: show domain (if multiple) and group (first OU) select */}
              {selectedType === 'Active Directory' && (
                <div>
                  {/* Domain select only when more than one domain exists */}
                  {adDomains.length > 1 && (
                    <div style={{ marginBottom: 8 }}>
                      <Select
                        options={adDomains.map((d) => ({ label: d, value: d }))}
                        value={selectedADDomain ? { label: selectedADDomain, value: selectedADDomain } : null}
                        onChange={(s) => {
                          setSelectedADDomain(s ? s.value : null);
                          // reset selected groups when domain changes
                          setSelectedADGroups([]);
                        }}
                        styles={selectStyles}
                        className="react-select-container w-full"
                        classNamePrefix="react-select"
                        placeholder="Select domain"
                      />
                    </div>
                  )}

                  {/* Groups: derive from selected domain (or single domain if only one) */}
                  <Select
                    isMulti
                    options={
                      (selectedADDomain
                        ? adGroupsByDomain[selectedADDomain]
                        : adDomains.length === 1
                          ? adGroupsByDomain[adDomains[0]]
                          : []
                      )?.map((g) => ({ label: g, value: g })) || []
                    }
                    value={selectedADGroups}
                    onChange={(selected) => {
                      setSelectedADGroups(selected || []);
                      setSearchValues(selected || []);
                    }}
                    styles={selectStyles}
                    className="react-select-container w-full"
                    classNamePrefix="react-select"
                    placeholder="Select"
                  />
                </div>
              )}
            </div>

            {/* Show Devices Button */}
            <div>
              <button className={`${Styles.show_btn} w-full lg:w-40 h-12`} onClick={handleShowDevices}>
                Show Devices
              </button>
            </div>
          </div>

          {/* OS Flavour Table (and reused for AD results) */}
          {showDevices && (selectedType === 'OS Flavour' || selectedType === 'Active Directory') && (
            <div className="mt-8 overflow-x-auto">
              <div className="flex justify-between items-center mb-1">
                <div className="text-normal">
                  Selected Devices ({selectedType})
                  <span className="ml-20">
                    Selected count: <strong>{selectedDeviceIds.length}</strong>
                  </span>
                </div>

                <input
                  type="text"
                  placeholder="Search devices..."
                  value={osSearchQuery}
                  onChange={(e) => setOsSearchQuery(e.target.value)}
                  className="px-3 py-1 rounded-md border border-gray-500 bg-transparent text-white placeholder-gray-400 focus:outline-none"
                  style={{ width: '40%' }}
                />
              </div>

              <DataTable
                columns={osColumns}
                data={paginatedOsAgents}
                getRowId={(agent) => agent.agentId}
                containerStyle={{
                  marginTop: '25px',
                  borderBottom: selectedDeviceIds.length > 0 ? '3px solid #00ff00' : '3px solid transparent',
                  transition: 'border-color 0.3s ease',
                }}
              />

              <Pagination
                currentPage={osCurrentPage}
                totalPages={osTotalPages}
                loading={heartbeatLoading}
                onPageChange={(page: number) => setOsCurrentPage(page)}
              />
            </div>
          )}

          {/* Group Table (unchanged) */}
          {showDevices && selectedType === 'Group' && (
            <div className="mt-8 overflow-x-auto" style={{ borderRadius: '10px' }}>
              <div className="flex justify-between items-center mb-1">
                <div className="text-normal">
                  Selected Devices (Group)
                  <span className="ml-20">
                    Selected count: <strong>{selectedAgentIds.length}</strong>
                  </span>
                </div>

                <input
                  type="text"
                  placeholder="Search devices..."
                  value={groupSearchQuery}
                  onChange={(e) => setGroupSearchQuery(e.target.value)}
                  className="px-3 py-1 rounded-md border border-gray-500 bg-transparent text-white placeholder-gray-400 focus:outline-none "
                  style={{ width: '40%' }}
                />
              </div>

              <DataTable
                columns={groupColumns}
                data={paginatedDevices}
                getRowId={(agent) => agent.tenantAgentId}
                containerStyle={{ marginTop: '15px' }}
              />

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                loading={heartbeatLoading}
                onPageChange={(page: number) => setCurrentPage(page)}
              />
            </div>
          )}

          {selectionError && <div style={{ color: 'red', marginTop: '4px' }}>{selectionError}</div>}

          {/* Buttons */}
          <div className="mt-8 flex justify-end gap-3 mb-7 mr-5 ">
            <button className="w-40 px-6 py-2 border border-gray-500 rounded-[30px] hover:bg-gray-700" onClick={handleCancelBtn}>
              Cancel
            </button>
            <button className="w-40 px-6 py-2 bg-red-600 rounded-[30px] shadow-md hover:bg-red-700" onClick={handleNext}>
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectDevice;
